// @spec GHG-STR-006
// @spec GHG-STR-040
/**
 * TypeScript types for GHG settings API
 */

// ============================================================================
// Calculation Method Types
// ============================================================================

/**
 * Calculation method options for Scope 3 categories per GHG Protocol
 */
export type CalculationMethod = 'activity' | 'spend' | 'hybrid';

/**
 * Global Warming Potential version options
 */
export type GwpVersion = 'ar5' | 'ar6';

// ============================================================================
// Scope 3 Category Types
// ============================================================================

/**
 * Configuration for a single Scope 3 category
 */
export interface Scope3CategoryConfig {
  enabled: boolean;
  calculationMethod: CalculationMethod;
}

/**
 * All 15 Scope 3 category keys
 */
export type Scope3CategoryKey =
  | 'purchasedGoodsServices'
  | 'capitalGoods'
  | 'fuelEnergyActivities'
  | 'upstreamTransportation'
  | 'wasteOperations'
  | 'businessTravel'
  | 'employeeCommuting'
  | 'upstreamLeasedAssets'
  | 'downstreamTransportation'
  | 'processingSoldProducts'
  | 'useSoldProducts'
  | 'endOfLifeSoldProducts'
  | 'downstreamLeasedAssets'
  | 'franchises'
  | 'investments';

/**
 * All 15 Scope 3 category configurations
 */
export interface Scope3Categories {
  purchasedGoodsServices: Scope3CategoryConfig;
  capitalGoods: Scope3CategoryConfig;
  fuelEnergyActivities: Scope3CategoryConfig;
  upstreamTransportation: Scope3CategoryConfig;
  wasteOperations: Scope3CategoryConfig;
  businessTravel: Scope3CategoryConfig;
  employeeCommuting: Scope3CategoryConfig;
  upstreamLeasedAssets: Scope3CategoryConfig;
  downstreamTransportation: Scope3CategoryConfig;
  processingSoldProducts: Scope3CategoryConfig;
  useSoldProducts: Scope3CategoryConfig;
  endOfLifeSoldProducts: Scope3CategoryConfig;
  downstreamLeasedAssets: Scope3CategoryConfig;
  franchises: Scope3CategoryConfig;
  investments: Scope3CategoryConfig;
}

// ============================================================================
// Scope 1 & 2 Coverage Types
// ============================================================================

/**
 * Configuration for a single scope coverage item
 */
export interface ScopeCoverageConfig {
  enabled: boolean;
}

/**
 * Scope 1 emission source coverage keys
 */
export type Scope1CoverageKey =
  | 'stationaryCombustion'
  | 'mobileCombustion'
  | 'fugitiveEmissions'
  | 'processEmissions';

/**
 * Scope 1 emission source coverage settings
 */
export interface Scope1Coverage {
  stationaryCombustion: ScopeCoverageConfig;
  mobileCombustion: ScopeCoverageConfig;
  fugitiveEmissions: ScopeCoverageConfig;
  processEmissions: ScopeCoverageConfig;
}

/**
 * Scope 2 emission source coverage keys
 */
export type Scope2CoverageKey = 'electricity' | 'heat' | 'cooling' | 'steam';

/**
 * Scope 2 emission source coverage settings
 */
export interface Scope2Coverage {
  electricity: ScopeCoverageConfig;
  heat: ScopeCoverageConfig;
  cooling: ScopeCoverageConfig;
  steam: ScopeCoverageConfig;
}

/**
 * Notes for a scope (assumptions and comments)
 */
export interface ScopeNotes {
  assumptions: string;
  comments: string;
}

// ============================================================================
// Settings Types
// ============================================================================

/**
 * Emission factor preference settings
 */
export interface EmissionFactorPreferences {
  preferredGwp: GwpVersion;
  bulkImportAutoApproveConfidenceThreshold: number;
}

/**
 * Approval workflow settings
 */
export interface ApprovalSettings {
  requireApprovalForDataEntry: boolean;
  approvalEmissionsThreshold: number;
  approvalScopeFilter: number[] | null;
}

/**
 * Notification preference settings
 */
export interface NotificationPreferences {
  dataCollectionReminders: boolean;
}

/**
 * Fallback assumptions for financial impact calculations.
 */
export interface FinancialImpactAssumptions {
  projectedSavingsRate: number;
  avoidedCarbonCostPerTco2e: number;
  npvRate: number;
}

/**
 * Full GHG settings response from API
 */
export interface GHGSettingsResponse {
  id: string;
  organizationId: string;
  scope1Coverage: Scope1Coverage;
  scope2Coverage: Scope2Coverage;
  scope1Notes: ScopeNotes;
  scope2Notes: ScopeNotes;
  scope3Categories: Scope3Categories;
  scope1Defaults: Record<string, unknown>;
  emissionFactorPreferences: EmissionFactorPreferences;
  approvalSettings: ApprovalSettings;
  notificationPreferences: NotificationPreferences;
  financialImpactAssumptions: FinancialImpactAssumptions;
  createdAt: string;
  updatedAt: string;
}

// ============================================================================
// Update Types
// ============================================================================

/**
 * Update schema for a single Scope 3 category
 */
export interface UpdateScope3CategoryConfig {
  enabled?: boolean | undefined;
  calculationMethod?: CalculationMethod | undefined;
}

/**
 * Update schema for Scope 3 categories (partial update)
 */
export type UpdateScope3Categories = Partial<Record<Scope3CategoryKey, UpdateScope3CategoryConfig>>;

/**
 * Update schema for GHG settings request
 */
export type UpdateScope1Coverage = Partial<Record<Scope1CoverageKey, Partial<ScopeCoverageConfig>>>;
export type UpdateScope2Coverage = Partial<Record<Scope2CoverageKey, Partial<ScopeCoverageConfig>>>;

export interface UpdateGHGSettingsRequest {
  scope1Coverage?: UpdateScope1Coverage | undefined;
  scope2Coverage?: UpdateScope2Coverage | undefined;
  scope1Notes?: Partial<ScopeNotes> | undefined;
  scope2Notes?: Partial<ScopeNotes> | undefined;
  scope3Categories?: UpdateScope3Categories | undefined;
  scope1Defaults?: Record<string, unknown> | undefined;
  emissionFactorPreferences?: Partial<EmissionFactorPreferences> | undefined;
  approvalSettings?: Partial<ApprovalSettings> | undefined;
  notificationPreferences?: Partial<NotificationPreferences> | undefined;
  financialImpactAssumptions?: Partial<FinancialImpactAssumptions> | undefined;
}

// ============================================================================
// Scope 3 Category Metadata
// ============================================================================

/**
 * Metadata for a Scope 3 category (for display purposes)
 */
export interface Scope3CategoryMeta {
  key: Scope3CategoryKey;
  number: number;
  name: string;
  description: string;
  defaultMethod: CalculationMethod;
  isUpstream: boolean;
}

/**
 * Complete metadata for all 15 Scope 3 categories
 */
export const SCOPE3_CATEGORIES_META: Scope3CategoryMeta[] = [
  {
    key: 'purchasedGoodsServices',
    number: 1,
    name: 'Purchased Goods & Services',
    description: 'Cradle-to-gate emissions from purchased goods and services',
    defaultMethod: 'hybrid',
    isUpstream: true,
  },
  {
    key: 'capitalGoods',
    number: 2,
    name: 'Capital Goods',
    description: 'Emissions from capital equipment purchases',
    defaultMethod: 'spend',
    isUpstream: true,
  },
  {
    key: 'fuelEnergyActivities',
    number: 3,
    name: 'Fuel & Energy Activities',
    description: 'Upstream emissions from purchased fuels and electricity',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'upstreamTransportation',
    number: 4,
    name: 'Upstream Transportation',
    description: 'Third-party transportation of purchased goods',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'wasteOperations',
    number: 5,
    name: 'Waste in Operations',
    description: 'Disposal of operational waste',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'businessTravel',
    number: 6,
    name: 'Business Travel',
    description: 'Employee travel for business purposes',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'employeeCommuting',
    number: 7,
    name: 'Employee Commuting',
    description: 'Employee travel to and from work',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'upstreamLeasedAssets',
    number: 8,
    name: 'Upstream Leased Assets',
    description: 'Assets leased by the organization',
    defaultMethod: 'activity',
    isUpstream: true,
  },
  {
    key: 'downstreamTransportation',
    number: 9,
    name: 'Downstream Transportation',
    description: 'Transportation of sold products',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'processingSoldProducts',
    number: 10,
    name: 'Processing of Sold Products',
    description: 'Processing of intermediate products',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'useSoldProducts',
    number: 11,
    name: 'Use of Sold Products',
    description: 'Emissions from product use',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'endOfLifeSoldProducts',
    number: 12,
    name: 'End-of-Life Treatment',
    description: 'Disposal of sold products',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'downstreamLeasedAssets',
    number: 13,
    name: 'Downstream Leased Assets',
    description: 'Assets leased to others',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'franchises',
    number: 14,
    name: 'Franchises',
    description: 'Emissions from franchise operations',
    defaultMethod: 'activity',
    isUpstream: false,
  },
  {
    key: 'investments',
    number: 15,
    name: 'Investments',
    description: 'Emissions from investments',
    defaultMethod: 'activity',
    isUpstream: false,
  },
];

/**
 * Labels for calculation methods
 */
export const CALCULATION_METHOD_LABELS: Record<CalculationMethod, string> = {
  activity: 'Activity-Based',
  spend: 'Spend-Based',
  hybrid: 'Hybrid',
};

/**
 * Get category metadata by key
 */
export function getCategoryMeta(key: Scope3CategoryKey): Scope3CategoryMeta | undefined {
  return SCOPE3_CATEGORIES_META.find((cat) => cat.key === key);
}

/**
 * Get upstream categories (1-8)
 */
export function getUpstreamCategories(): Scope3CategoryMeta[] {
  return SCOPE3_CATEGORIES_META.filter((cat) => cat.isUpstream);
}

/**
 * Get downstream categories (9-15)
 */
export function getDownstreamCategories(): Scope3CategoryMeta[] {
  return SCOPE3_CATEGORIES_META.filter((cat) => !cat.isUpstream);
}
